import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import styled from 'styled-components';
import { COLORS } from '../Styles/Colors';
import { IoIosPin } from 'react-icons/io';
import { Company, CompanyFormInput, Report, USER_ROLE } from '../Types';
import useGetData from '../Hooks/useGetData';
import { AddButton, CancelButton, CloseButton, DeleteButton, DeleteConfirmButton, EditButton, ErrorMessage, GridContainer, LoadingMessage, Message, ModalContent, ModalHeader, ModalOverlay, PageContainer, RecordCount, SubmitButton, Title } from '../Styles/AppStyles';
import Cookies from 'js-cookie';
import { decode, decodeToken } from '../Services/DecodeToken';
import { useSnackbar } from '../Context/SnackbarProvider';
import { ColumnDef, DataTable } from '../Components/Table/DataTable';
import { Header } from '../Components/Header/Header';
import { formatUTC } from '../Services/FormatUtc';
import ReportsIcon from '../Components/Icons/ReportsIcon';
import dayjs from 'dayjs';
import { Box, Button, MenuItem, Select, TextField, Typography, InputAdornment } from '@mui/material';
import useGetCustomData from '../Hooks/useGetCustomData';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateRangePicker } from '../Components/Date/Datepicker';
import apiClient from '../Services/ApiClient';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { SearchableHeader } from '../Components/Search/SearchableHeader';
import { useColumnSearches } from '../Hooks/useColumnSearch';
import { LocationSelect } from '../Components/Select/LocationSelect';

const TypedIoIosPin = IoIosPin as unknown as React.FC<{ size?: number | string }>;


const AbatixAdminAuditReport: React.FC = () => {
  const token: any = decode(); 
  const id = token.userId;
  
  const [startDate, setStartDate] = useState<Date | null>(dayjs().subtract(7, 'day').toDate());
  const [endDate, setEndDate] = useState<Date | null>(null);

  const [ovs] = useGetCustomData('/api/location/user/ovs');
  const [selectedOvs, setSelectedOvs] = useState<string>('');

  const searchFields = [
  'UserName',
  'ProductName',
  'Product_code',
  'P21OrderNumber',
  'P21InvoiceNumber',
  'VendingLocationId',
  'Note',
  'Key1CD',
  'Key1Value',
  'Key2CD',
  'Key2Value',
  'Key3CD',
  'Key3Value',
];


const {
  columnSearches,
  setSearch,
  clearSearch,
  clearAllSearches,
} = useColumnSearches(searchFields);



  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const baseUrl = useMemo(() => {
    const params = new URLSearchParams();
  
    if (startDate) {
      params.set('startDate', dayjs(startDate).format('YYYY-MM-DD'));
    }
  
    if (endDate) {
      params.set('endDate', dayjs(endDate).format('YYYY-MM-DD'));
    }

    if (selectedOvs) {
      params.set('ovid', selectedOvs);
    }

    Object.entries(columnSearches).forEach(([field, value]) => {
      if (value.trim()) {
        params.set(`${field}`, value.trim());
      }
    });
  
    return `/api/reports/transaction-audit?${params.toString()}`;
  }, [startDate, endDate, selectedOvs, columnSearches]);

  const paginatedUrl = `${baseUrl}&limit=${pageSize}&offset=${page * pageSize}`;
  const [reports, loading, errorFetchReport, refetch] = useGetCustomData(paginatedUrl);

  const [displayedReports, setDisplayedReports] = useState<any[]>([]);


   useEffect(() => {
  if (reports?.data) {
    const formatted = reports.data.map((report: any) => ({
      ...report,
      Created_At: formatUTC(report.Created_At),
      ModifiedDate: formatUTC(report.ModifiedDate),
    }));
    setDisplayedReports(formatted);
  }
}, [reports]);


  const handleFilter = () => {
    setPage(0); 
  };

  const handleColumnSearch = (field: string, value: string) => {
    setSearch(field, value);
    setPage(0); 
  };

  const handleClearColumnSearch = (field: string) => {
    clearSearch(field)
    setPage(0);
  };

 

  const getActiveSearchCount = () => {
    return Object.values(columnSearches).filter(value => value.trim() !== '').length;
  };

 


  const totalRows = reports?.totalCount ?? 0;

  const {showSnackbar} = useSnackbar();
  const [editedCells, setEditedCells] = useState<Record<string, Record<string, number | ''>>>({});
  const [activeEdit, setActiveEdit] = useState<{id: string, field: string} | null>(null);

  useEffect(() => {
    const handleClickOutside = () => {
      if (activeEdit) {
        setActiveEdit(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeEdit]);
  

  const handleCancelChanges = () => {
    setEditedCells({});
    setActiveEdit(null);
  };

  const hasEdits = Object.keys(editedCells).length > 0;

  const columns: ColumnDef<Report>[] = [

        { 
      field: 'ProductName', 
      headerName: 'Product', 
      width: 150, 
      sortable: false,
      searchable: true,
      renderHeader: () => (
        <SearchableHeader
          field="ProductName"
          headerName="Product"
          searchValue={columnSearches.ProductName}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      )
    },
    { 
      field: 'Product_code', 
      headerName: 'Product Code', 
      width: 170, 
      sortable: false,
      searchable: true,
      renderHeader: () => (
        <SearchableHeader
          field="Product_code"
          headerName="Product Code"
          searchValue={columnSearches.Product_code}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      )
    },
    { 
      field: 'UserName', 
      headerName: 'User', 
      width: 150, 
      sortable: false,
      searchable: true,
      renderHeader: () => (
        <SearchableHeader
          field="UserName"
          headerName="User"
          searchValue={columnSearches.UserName}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      )
    },

    { field: 'PreviousQty', headerName: 'Previous Qty', width: 130, sortable: false, renderCell: (params) => renderQuantityCell(params, 'PreviousQty')  },
    { field: 'UpdatedQty', headerName: 'Updated Qty', width: 130, sortable: false, renderCell: (params) => renderQuantityCell(params, 'UpdatedQty') },
    { field: 'MinimumQty', headerName: 'Minimum Qty', width: 130 , sortable: false,  renderCell: (params) => renderQuantityCell(params, 'MinimumQty')  },
    { field: 'MaximumQty', headerName: 'Maximum Qty', width: 130 , sortable: false, renderCell: (params) => renderQuantityCell(params, 'MaximumQty')   },
    { field: 'Created_At', headerName: 'Created At', width: 160, sortable: false  },
    { field: 'ModifiedDate', headerName: 'Modified Date', width: 160, sortable: false },
    { 
      field: 'VendingLocationId', 
      headerName: 'Vending Location ID', 
      width: 200,
      searchable: true,
    },
    { 
      field: 'Note', 
      headerName: 'Note', 
      width: 220, 
      sortable: false,
      searchable: true,
    
    },
    { field: 'NotSyncOrderQty', headerName: 'Not Synced Order Qty', width: 180  , sortable: false },
    { 
      field: 'P21OrderNumber', 
      headerName: 'P21 Order Number', 
      width: 180, 
      sortable: false,
      searchable: true,

    },
    { 
      field: 'P21InvoiceNumber', 
      headerName: 'P21 Invoice Number', 
      width: 180, 
      sortable: false,
      searchable: true,
   
    },
    { 
      field: 'Key1CD', 
      headerName: 'Key 1 Code', 
      width: 140, 
      sortable: false,
      searchable: true,
     
    },
    { 
      field: 'Key1Value', 
      headerName: 'Key 1 Value', 
      width: 140, 
      sortable: false,
      searchable: true,
     
    },
    { 
      field: 'Key2CD', 
      headerName: 'Key 2 Code', 
      width: 140, 
      sortable: false,
      searchable: true,
     
    },
    { 
      field: 'Key2Value', 
      headerName: 'Key 2 Value', 
      width: 140, 
      sortable: false,
      searchable: true,
     
    },
    { 
      field: 'Key3CD', 
      headerName: 'Key 3 Code', 
      width: 140, 
      sortable: false,
      searchable: true,
    
    },
    { 
      field: 'Key3Value', 
      headerName: 'Key 3 Value', 
      width: 140, 
      sortable: false,
      searchable: true,
     
    },
  ];



  const handleQuantityChange = (id: string, field: string, value: number | '') => {
  setEditedCells(prev => ({
    ...prev,
    [id]: {
      ...(prev[id] || {}),
      [field]: value,
    },
  }));
};

  const renderQuantityCell = useCallback((params: any, field: string) => {


    const isEdited = editedCells[params.id]?.[field] !== undefined;
    const isActive = activeEdit?.id === params.id && activeEdit?.field === field;
    const displayValue = isEdited ? editedCells[params.id][field] : params.value;
  
    const handleCellClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setActiveEdit({ id: params.id, field });
    };
  
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          padding: '0 8px',
          backgroundColor: isEdited ? `${COLORS.teal}15` : 'transparent',
          position: 'relative',
        }}
        onClick={handleCellClick}
      >
        {isActive ? (
          <input
            type="number"
            value={displayValue === '' ? '' : String(displayValue)}
            onChange={(e) => {
              const raw = e.target.value;
              const parsed = raw === '' ? '' : Number(raw);
              handleQuantityChange(params.id, field, parsed);
            }}
            disabled={token.roles.level < 50}
            autoFocus
            style={{
              width: '100%',
              borderRadius: '4px',
              padding: '4px',
              outline: 'none',
              border: 'none',
              backgroundColor: 'white',
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'Escape') {
                setActiveEdit(null);
              }
            }}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                fontWeight: isEdited ? 'bold' : 'normal',
                color: isEdited ? COLORS.teal : 'inherit',
              }}
            >
              {displayValue === '' ? '-' : displayValue}
            </span>
            {isEdited && (
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: COLORS.teal,
                  marginLeft: '8px',
                }}
              />
            )}
          </div>
        )}
      </div>
    );
  }, [editedCells, activeEdit, token.roles.level]);


  const handleSaveChanges = async () => {
  const fullPayload = [];

  for (const [id, fields] of Object.entries(editedCells)) {
    const original = displayedReports?.find((item: any) => item.ID == id);

    const mergedFields = {
      UpdatedQty: fields.UpdatedQty ?? original?.UpdatedQty,
      MinimumQty: fields.MinimumQty ?? original?.MinimumQty,
      MaximumQty: fields.MaximumQty ?? original?.MaximumQty,
      PreviousQty: fields.PreviousQty ?? original?.PreviousQty,
    };

    fullPayload.push({
      id: Number(id),
      ...mergedFields,
    });
  }



  const response = await apiClient('/api/reports/edit/bulk', {
    method: 'PUT',
    body: JSON.stringify(fullPayload),
  });

  if (!response.ok) {
    const errorData = await response.json();
    showSnackbar("Failed to save", "error", "Something went wrong!");
    return;
  }

  const updatedData = displayedReports.map((item: any) => {
      const edits = editedCells[item.id];
      if (!edits) return item;
  
      return {
        ...item,
        ...edits,
      };
    });
  
    setDisplayedReports(updatedData);


    showSnackbar("Changes saved sucessfully", "success");
    setEditedCells({});
    setActiveEdit(null);

};


  return (
    <PageContainer>
      <Header title='Transaction Audit Report' count={totalRows} hideCount={true}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '16px' }}>
          {hasEdits && (
            <div style={{ display: 'flex', gap: '10px' }}>
              <SubmitButton
                onClick={handleSaveChanges}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <span>Save Changes</span>
                <div
                  style={{
                    fontSize: '10px',
                    backgroundColor: 'white',
                    color: COLORS.teal,
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  {Object.keys(editedCells).length}
                </div>
              </SubmitButton>
              <CancelButton onClick={handleCancelChanges}>Cancel</CancelButton>
            </div>
          )}
        </div>

        <DateRangePicker startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate}/>
 
        {getActiveSearchCount() > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '16px' }}>
            <Typography variant="body2" style={{ color: COLORS.teal }}>
              {getActiveSearchCount()} column search{getActiveSearchCount() > 1 ? 'es' : ''} active
            </Typography>
            <Button
              size="small"
              onClick={clearAllSearches}
              style={{ 
                color: COLORS.teal, 
                textTransform: 'none',
                minWidth: 'auto',
                padding: '2px 8px'
              }}
            >
              Clear All Searches
            </Button>
          </div>
        )}
      </Header>
    
      <DataTable 
        data={displayedReports}
        columns={columns}
        title='Transaction Audit Report'
        pageKey="abatix-admin-audit-report"
        loading={loading}
        serverSidePagination={true}
        page={page}
        idField='ID'
        rowCount={totalRows}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        initialPageSize={pageSize}
        hideColumnFilters={true}
        hideSearchBar={true}
      >
          
      </DataTable>
    </PageContainer>
  );
};

export default AbatixAdminAuditReport;